import { useRef, useState } from "react";
import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Errors } from "../requests/controls";
import RescheduleDetails from "./details";
import type { Offer } from "./types";
export default function RescheduleView({
  offer,
  decision_url,
}: {
  offer: Offer;
  decision_url: string;
}) {
  const { t } = useTranslation();
  const { customer_history_path } = usePage<{
    customer_history_path?: string;
  }>().props;
  const [secondary, setSecondary] = useState<"rescheduled" | "declined" | null>(
    null,
  );
  const trigger = useRef<HTMLButtonElement>(null);
  const { flash } = usePage<{ flash: { alert?: string; notice?: string } }>()
    .props;
  return (
    <PageCanvas as="main">
      {customer_history_path && (
        <Link className="inline-block underline" href={customer_history_path}>
          {t("customer_portal.history")}
        </Link>
      )}
      <Head title={t("reschedules.title")} />
      <h1 className="text-page font-display">{t("reschedules.title")}</h1>
      {flash.alert && <p role="alert">{flash.alert}</p>}
      {flash.notice && <p role="status">{flash.notice}</p>}
      <Errors
        targets={{
          suggested_time: "reschedule-alternative",
          note: "reschedule-note",
        }}
      />
      <RescheduleDetails offer={offer} />
      {offer.writable ? (
        <section className="space-y-5" aria-label={t("proposals.decision")}>
          <Form
            key={`accepted-${offer.revision}`}
            action={decision_url}
            method="post"
          >
            {({ processing }) => (
              <>
                <input type="hidden" name="revision" value={offer.revision} />
                <input type="hidden" name="decision[action]" value="accepted" />
                <Button type="submit" disabled={processing}>
                  {t("reschedules.accepted")}
                </Button>
              </>
            )}
          </Form>
          <div className="flex flex-wrap gap-3">
            {(["rescheduled", "declined"] as const).map((action) => (
              <Button
                key={action}
                type="button"
                variant="outline"
                aria-expanded={secondary === action}
                aria-controls={`reschedule-secondary-${action}`}
                onClick={(event) => {
                  trigger.current = event.currentTarget;
                  setSecondary(action);
                }}
              >
                {t(`reschedules.${action}`)}
              </Button>
            ))}
            {secondary && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSecondary(null);
                  trigger.current?.focus();
                }}
              >
                {t("common.cancel")}
              </Button>
            )}
          </div>
          {secondary && (
            <Form
              id={`reschedule-secondary-${secondary}`}
              key={`${secondary}-${offer.revision}`}
              action={decision_url}
              method="post"
              className="space-y-3 rounded-lg border p-4"
            >
              {({ processing }) => (
                <>
                  <input type="hidden" name="revision" value={offer.revision} />
                  <input
                    type="hidden"
                    name="decision[action]"
                    value={secondary}
                  />
                  {secondary === "rescheduled" ? (
                    <>
                      <Label htmlFor="reschedule-alternative">
                        {t("proposals.alternative")}
                      </Label>
                      <Textarea
                        autoFocus
                        id="reschedule-alternative"
                        name="decision[suggested_time]"
                        required
                        maxLength={500}
                      />
                      <p className="text-muted-foreground text-sm">
                        {t("reschedules.counter_help")}
                      </p>
                    </>
                  ) : (
                    <>
                      <Label htmlFor="reschedule-note">
                        {t("proposals.decline_note")}
                      </Label>
                      <Textarea
                        autoFocus
                        id="reschedule-note"
                        name="decision[note]"
                        maxLength={500}
                      />
                    </>
                  )}
                  <Button variant="outline" type="submit" disabled={processing}>
                    {t(`reschedules.${secondary}`)}
                  </Button>
                </>
              )}
            </Form>
          )}
        </section>
      ) : (
        offer.state === "pending" && (
          <p role="status">{t("proposals.read_only")}</p>
        )
      )}
      <p className="text-muted-foreground text-sm">
        {t("proposals.view_help")}
      </p>
    </PageCanvas>
  );
}
